import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Stack, Popover, Typography, Card, CardContent, Skeleton } from "@mui/material";
import { DataGridPro, GridColDef, GridDensity, GridFilterModel, GridPaginationModel, GridSortModel, GridToolbar } from "@mui/x-data-grid-pro";
import { tagAPI, useGetTagsForSearchTermQuery, useGetTagsListQuery, useGetTagsQuery, useTagSearchTermMutation, useTagSearchTermsWithTagsBulkMutation, useUntagSearchTermMutation } from "src/context/api/tags/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { useSelector } from "react-redux";
import { fDate } from "src/utils/format-time";
import { useEffect, useMemo, useRef, useState } from "react";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { formatNumber } from "src/utils/format-number";
import { formatCurrency } from "src/utils/format-currency";
import { useLocales } from "src/locales";
import Iconify from "src/components/iconify";
import "./datagrid.scss";
import AddTagCell, { generateColorFromString } from "./add-tag-cell";
import GenericInfiniteScrollList from "../../generic-infinite-scroll-list";
import { enqueueSnackbar } from "notistack";
import { useForm } from "react-hook-form";
import FormProvider from "src/components/hook-form";
import { FilterPanelCustom } from "src/components/data-grid/filter-panel-custom";
import { useBoolean } from "src/hooks/use-boolean";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Scrollbar from "src/components/scrollbar";

const schema = yup.object().shape({
    search_term: yup.string().required(),
    tags: yup.array().of(yup.string().required()).required().min(1),
});

const TagsCell = (props: any & { tags: any[], refetchTags: () => void }) => {
    const workspace = useCurrentWorkspace();
    const MAX_VISIBLE_TAGS = 3;
    const visibleTags = props.tags.slice(0, MAX_VISIBLE_TAGS);
    const remainingCount = props.tags.length - MAX_VISIBLE_TAGS;
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [untagSearchTerm] = useUntagSearchTermMutation();
    const [tagSearchTerm] = useTagSearchTermMutation();

    const { data: { tags: tagsList = [] } = {} } = useGetTagsListQuery({ workspaceId: workspace?.id as number }, { skip: !workspace?.id });
    const { data: { tags: tagsForSearchTerm = [] } = {} } = useGetTagsForSearchTermQuery({ workspaceId: workspace?.id as number, searchTerm: props.row.id }, { skip: !workspace?.id || !props.row.id });
    const addTagsModal = useBoolean(false);
    const defaultValues = {
        search_term: props.row.id,
        tags: [] as string[],
    };
    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues,
    });

    const [tagsOptions, setTagsOptions] = useState<any[]>([]);
    const hasFilledTags = useRef(false);
    const hasCurrentTags = useRef(false);

    useEffect(() => {
        setTagsOptions((prev) => [...prev, ...(tagsList?.filter((tag: any) => !tagsOptions.some((t: any) => t.label === tag.label)).map((tag: any) => ({
            id: tag.id,
            label: tag.name,
        }))) || []]);
    }, [tagsList]);

    useEffect(() => {
        if (tagsForSearchTerm?.length !== methods.getValues("tags").length) {
            methods.setValue("tags", tagsForSearchTerm?.map((tag: any) => tag.name) || [] as string[]);
        }
        if (hasCurrentTags.current) return;
        if (tagsForSearchTerm?.length > 0) {
            hasCurrentTags.current = true;
            setTagsOptions((prev) => [...prev, ...(tagsForSearchTerm?.map((tag: any) => ({
                id: tag.id,
                label: tag.name,
            }))) || []]);
            methods.setValue("tags", tagsForSearchTerm?.map((tag: any) => tag.name) || [] as string[]);
        }
    }, [tagsForSearchTerm]);

    const deduplicateTagsOptions = useMemo(() => {
        return Array.from(new Set(tagsOptions.map((tag: any) => tag.label))).map((label: string) => tagsOptions.find((tag: any) => tag.label === label));
    }, [tagsOptions]);

    const onSubmit = (data: any) => {
        tagSearchTerm({ workspaceId: workspace?.id as number, searchTerm: props.row.id, tags: data.tags.map((tag: any) => ({ label: tag })) })
            .unwrap()
            .then(() => {
                addTagsModal.onFalse();
                methods.reset();
                enqueueSnackbar("Searchterm tagged successfully", { variant: "success" });
            })
            .catch((error) => {
                enqueueSnackbar("Searchterm tagging failed", { variant: "error" });
            });
    }

    const open = Boolean(anchorEl);

    return (
        <Stack direction="row" justifyContent="space-between" height="100%" gap={0} sx={{ overflow: 'hidden', width: '100%', px: 0 }}>
            <Scrollbar sx={{ width: '100%', height: '100%' }}>
                <Stack width="fit-content" direction="row" alignItems="center" py={1.5} px={1} gap={1}>

                    {props.tags.length > 0 ? (
                        props.tags.map((tag: any) => (
                            <Chip
                                key={tag.id}
                                label={tag.name}
                                size="small"
                                variant="soft"
                                sx={{
                                    pl: 1,
                                    gap: 1,
                                    backgroundColor: "rgba(145, 158, 171, 0.1)",
                                    color: "rgba(94, 109, 123, 1)",
                                    maxWidth: '150px',
                                    '& .MuiChip-label': {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        color: "rgba(94, 109, 123, 1)"
                                    },
                                    "& .MuiChip-label.MuiChip-labelSmall": {
                                        color: 'rgba(94, 109, 123, 1)!important'
                                    }
                                }}
                                onDelete={() => {
                                    untagSearchTerm({ workspaceId: workspace?.id as number, searchTerm: props.row.id, tag: tag.name })
                                        .unwrap()
                                        .then(() => {
                                            enqueueSnackbar("Search term untagged successfully", { variant: "success" });
                                        })
                                        .catch(() => {
                                            enqueueSnackbar("Untagging search term failed", { variant: "error" });
                                        });
                                }}
                            />
                        ))
                    ) : (
                        <Typography sx={{ color: "rgba(94, 109, 123, 0.7)" }}>--</Typography>
                    )}
                </Stack>
            </Scrollbar>

            <Button
                onClick={addTagsModal.onTrue}
                sx={{
                    borderRadius: 0,
                    m: 0,
                    p: 0,
                    color: "#2F3843",
                    backgroundColor: "rgba(240, 240, 240, 0.70)",
                    minWidth: 'fit-content',
                    "& .MuiButton-startIcon": {
                        ml: 0.5,
                        mr: 0.5,
                    },
                    "&:hover": {
                        backgroundColor: "rgba(240, 240, 240, 0.8)",
                    },
                }} variant="contained" startIcon={<Iconify sx={{ cursor: 'pointer' }} onClick={addTagsModal.onTrue} icon="mdi-light:plus" />} />
            <Dialog fullWidth maxWidth="sm" open={addTagsModal.value} onClose={addTagsModal.onFalse}>
                <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
                    <DialogTitle color={"rgba(51, 60, 71, 1)"}>Add tags</DialogTitle>
                    <DialogContent sx={{ p: 2 }}>
                        <TextField size="medium" label="Search term" disabled value={props.row.id} fullWidth sx={{ mt: 2 }} />
                        <GenericInfiniteScrollList name="tags"
                            sx={{ mt: 2 }}
                            size="medium"
                            searchPlaceholder="Search for a tag..."
                            fullWidth={true}
                            multiple={true}
                            freeSolo={true}
                            options={tagsOptions}
                            items={deduplicateTagsOptions}
                            transformValue={(valueId: string) => tagsOptions.find((tag: any) => tag.id === valueId)?.label || valueId as any}
                            customItemRenderer={(option) => (
                                <Chip
                                    label={option.label || option as any}
                                    size="small"
                                    variant="soft"
                                    sx={{
                                        backgroundColor: "rgba(145, 158, 171, 0.1)",
                                        color: "rgba(94, 109, 123, 1)"
                                    }}
                                />
                            )}
                            customTagRenderer={(option) => (
                                <Chip
                                    label={option.label || option as any}
                                    size="small"
                                    variant="soft"
                                    sx={{
                                        cursor: "pointer",
                                        marginRight: 0.5,
                                        backgroundColor: "rgba(145, 158, 171, 0.1)",
                                        color: "rgba(94, 109, 123, 1)"
                                    }}
                                    onDelete={() => {
                                        methods.setValue("tags", methods.getValues("tags").filter((tag: any) => tag.id !== option.id));
                                    }}
                                />
                            )}
                            setItems={(items) => setTagsOptions(items)}
                            getOptionLabel={(option) => (option as any).label || option as any}
                            getOptionKey={(option) => (option as any).id || option as any}
                            isOptionEqualToValue={(option, value) => {
                                const isOptionIDEqual = (option.id || option as any) === value;
                                const isOptionLabelEqual = (option.label || option as any) === value;
                                return isOptionIDEqual || isOptionLabelEqual;
                            }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'flex-start' }}>
                        <Button sx={{ backgroundColor: "#181818" }} variant="contained" color="primary" type="submit">Add tags</Button>
                    </DialogActions>
                </FormProvider>
            </Dialog>
        </Stack>
    );
};

const useColumns = (page: number, pageSize: number, refetchTags: () => void) => {
    const workspace = useCurrentWorkspace();
    const { currentLang } = useLocales();
    const locale = currentLang.value || "en";
    return [
        {
            field: 'id',
            headerName: 'Search Term',
            headerClassName: 'header-font-color',
            flex: 1,
        },
        {
            field: 'brand_searches',
            headerName: 'Brand Searches',
            headerClassName: 'header-font-color',
            type: 'number',
            flex: 1,
            align: 'right',
            valueFormatter: (value) => formatNumber(value)
        },
        {
            field: 'ad_cost',
            headerName: 'Ad Cost',
            headerClassName: 'header-font-color',
            type: 'number',
            flex: 1,
            align: 'right',
            valueFormatter: (value) => formatCurrency(value, workspace?.currency, locale, true, 2)
        },
        {
            field: 'blended_ctr',
            headerName: 'Blended CTR',
            headerClassName: 'header-font-color',
            type: 'number',
            flex: 1,
            align: 'right',
            valueFormatter: (value) => `${value}%`
        },
        {
            field: 'tags',
            headerClassName: 'header-font-color',
            headerName: 'Tags',
            flex: 1,
            minWidth: 400,
            sortable: false,
            cellClassName: 'tags-cell',
            renderCell: (params) => <TagsCell tags={params.row.tags || []} refetchTags={refetchTags} row={params.row} />,
        },
    ] as GridColDef[];
};

const CustomToolbar = (props: any & { multiTaggable: boolean, rowSelectionModel: GridRowSelectionModel, refetchTags: () => void }) => {
    const workspace = useCurrentWorkspace();
    const { data: { tags: tagsList = [] } = {} } = useGetTagsListQuery({ workspaceId: workspace?.id as number });
    const [tagsOptions, setTagsOptions] = useState<any[]>([]);
    const [tagSearchTermsWithTagsBulk] = useTagSearchTermsWithTagsBulkMutation();
    const [open, setOpen] = useState<boolean>(false);
    const handleClose = () => {
        setOpen(false);
    };

    const methods = useForm({
        defaultValues: {
            searchTerms: props.rowSelectionModel.map((row: any) => ({ id: row, label: row })),
            tags: [] as { label: string }[],
        },
    });

    useEffect(() => {
        if (tagsList?.length > 0 && tagsOptions.length === 0 || tagsList?.length != tagsOptions.length) {
            setTagsOptions(tagsList.map((tag: any) => ({ id: tag.id, label: tag.name })));
        }
    }, [tagsList]);


    const handleSubmit = (data: any) => {
        tagSearchTermsWithTagsBulk({ workspaceId: workspace?.id as number, tags: data.tags, searchTerms: data.searchTerms })
            .unwrap()
            .then(() => {
                handleClose();
                enqueueSnackbar("Search terms tagged successfully", { variant: "success" });
            })
            .catch((error) => {
                enqueueSnackbar("Tagging search terms failed", { variant: "error" });
            });
    }



    return (
        <>
            <Stack className="custom-toolbar" direction="row" px={0} mx={0} pr={2} alignItems="center" gap={1}>
                <GridToolbar {...props} />
                <Stack flexGrow={1} />
                <Button variant={"contained"} sx={{ backgroundColor: "#181818", px: 1.5, mx: 0, my: 1 }} disabled={!props.multiTaggable} onClick={() => setOpen(true)}>Tag Selection</Button>
            </Stack>
            <Dialog fullWidth maxWidth="sm" sx={{ padding: "24px" }} open={open} onClose={handleClose}>
                <DialogTitle sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'start', gap: "8px" }}>
                    <Typography sx={{
                        color: "#333C47",
                        fontFamily: "Public Sans",
                        fontSize: "18px",
                        fontWeight: 500,
                        lineHeight: "100%",
                    }} textAlign="left" variant="h6">Tag Selection</Typography>
                    <Typography sx={{
                        color: "#637381",
                        fontSize: "13px",
                        fontWeight: 400,
                        lineHeight: "100%",
                    }} variant="body1">Select the tags you want to add to the current selection</Typography>
                </DialogTitle>
                <DialogContent sx={{ p: "24px" }}>
                    <FormProvider methods={methods} onSubmit={methods.handleSubmit(handleSubmit)}>
                        <GenericInfiniteScrollList
                            fullWidth={true}
                            name="searchTerms"
                            sx={{ mb: 2, mt: "24px" }}
                            searchPlaceholder="Search terms selection"
                            options={props.rowSelectionModel.map((row: any) => ({ id: row, label: row }))}
                            items={props.rowSelectionModel.map((row: any) => ({ id: row, label: row }))}
                            getOptionLabel={(option) => (option as any).label || option as any}
                            getOptionKey={(option) => (option as any).id || option as any}
                            customTagRenderer={(option) => <Chip sx={{ cursor: "pointer", mx: 0.5 }} key={option.id || option as any} label={option.label || option as any} size="small" variant="soft" onClick={() => methods.setValue("searchTerms", methods.getValues("searchTerms").filter((term: any) => term.id !== option.id))} />}
                            customItemRenderer={(option) => <Chip key={option.id || option as any} label={option.label || option as any} size="small" variant="soft" />}
                            multiple={true}
                            size="medium"
                        />
                        <GenericInfiniteScrollList name="tags"
                            sx={{ mt: 2 }}
                            searchPlaceholder="Search for a tag"
                            fullWidth={true}
                            multiple={true}
                            freeSolo={true}
                            size="medium"
                            options={tagsOptions}
                            items={tagsOptions}
                            transformValue={(valueId: string) => tagsOptions.find((tag: any) => tag.id === valueId)?.label || valueId as any}
                            customItemRenderer={(option) => (
                                <Chip
                                    label={option.label || option as any}
                                    size="small"
                                    variant="soft"
                                    sx={{
                                        backgroundColor: option.label || option as any
                                            ? generateColorFromString(String(option.label || option))
                                            : undefined,
                                        color: "#fff"
                                    }}
                                />
                            )}
                            customTagRenderer={(option) => (
                                <Chip
                                    label={option.label || option as any}
                                    size="small"
                                    variant="soft"
                                    sx={{
                                        cursor: "pointer",
                                        marginRight: 0.5,
                                        backgroundColor: option.label || option as any
                                            ? generateColorFromString(String(option.label || option))
                                            : undefined,
                                        color: "#fff"
                                    }}
                                    onDelete={() => {
                                        methods.setValue("tags", methods.getValues("tags").filter((tag: any) => tag.id !== option.id));
                                    }}
                                />
                            )}
                            setItems={(items) => setTagsOptions(items)}
                            getOptionLabel={(option) => (option as any).label || option as any}
                            getOptionKey={(option) => (option as any).id || option as any}
                            isOptionEqualToValue={(option, value) => (option.id || option as any) === (value.id || value as any)}
                        />
                        <Button type="submit" sx={{ mt: "24px", backgroundColor: "#181818" }} variant="contained" color="primary">Tag Selection</Button>
                    </FormProvider>
                </DialogContent>
            </Dialog>
        </>
    )
}

export default function TagManagerGrid() {
    const workspace = useCurrentWorkspace();
    const filterStore = useSelector((state: any) => state.filters);
    const skipQuery = !filterStore.start || !filterStore.end || !workspace?.id;
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 1, pageSize: 10 });
    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: "brand_searches", sort: "desc" }]);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
    const [multiTaggable, setMultiTaggable] = useState<boolean>(false);
    const [density, setDensity] = useState<GridDensity>("standard");

    const { data, isLoading, isFetching, isUninitialized, refetch: refetchTags } = useGetTagsQuery({
        workspaceId: workspace?.id as number,
        start: fDate(filterStore.start, "yyyy-MM-dd"),
        end: fDate(filterStore.end, "yyyy-MM-dd"),
        keywords: filterStore.keywords,
        countries: filterStore.countries,
        devices: filterStore.devices,
        platforms: filterStore.platforms,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        filterModel,
        tags: filterStore.tags.map((tag: any) => tag.label),
        sortField: sortModel[0]?.field,
        sortOrder: sortModel[0]?.sort as string,
    }, { skip: skipQuery });

    useEffect(() => {
        if (rowSelectionModel.length > 0) {
            setMultiTaggable(true);
        } else {
            setMultiTaggable(false);
        }
    }, [rowSelectionModel]);

    return (
        <Card sx={{
            "& .MuiCardContent-root:last-child": { paddingBottom: "0 !important" },
            border: "1px solid rgba(145, 158, 171, 0.20)",
            borderRadius: "8px"
        }}>
            <CardContent sx={{
                px: 0.1,
                py: 0,
                paddingBottom: "24px",
                overflow: "visible",
                minHeight: 600, // laisse la hauteur fixe pour éviter les sauts
                position: "relative"
            }}>
                {/* Skeleton superposé */}
                {(isLoading || isFetching || isUninitialized) && (
                    <Skeleton
                        variant="rectangular"
                        height="100%"
                        width="100%"
                        animation="wave"
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            zIndex: 10
                        }}
                    />
                )}


                <DataGridPro
                    sx={{
                        "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                            outline: "none",
                            backgroundColor: "transparent",
                        },
                        "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
                            outline: "none",
                            backgroundColor: "transparent",
                        },
                        "& .MuiTablePagination-root": {
                            backgroundColor: 'rgba(250, 250, 250, 1)!important'
                        },
                        opacity: (isLoading || isFetching || isUninitialized) ? 0.5 : 1
                    }}
                    classes={{
                        root: "search-term-grid",
                        columnHeaders: "search-term-grid-column-headers",
                        columnHeader: "search-term-grid-column-header",
                        "cellCheckbox": "cell-checkbox",
                    }}
                    pageSizeOptions={[10, 20, 50, 100]}
                    loading={isLoading || isFetching || isUninitialized}
                    rows={data?.data || []}
                    columns={useColumns(paginationModel.page, paginationModel.pageSize, refetchTags)}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    disableMultipleColumnsSorting={true}
                    rowSelectionModel={rowSelectionModel}
                    onRowSelectionModelChange={(newSelectionModel) => setRowSelectionModel([...newSelectionModel])}
                    disableRowSelectionOnClick
                    checkboxSelection
                    filterModel={filterModel}
                    onFilterModelChange={setFilterModel}
                    density={density}
                    onDensityChange={setDensity}
                    filterMode="server"
                    sortingMode="server"
                    sortingOrder={['desc', 'asc']}
                    getRowHeight={() => density === "compact" ? 40 : density === "comfortable" ? 60 : 50}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    slots={{
                        filterPanel: (props) => <FilterPanelCustom {...props} getColumnForNewFilter={(args) => args ? args.columns[0].field : undefined} />,
                        toolbar: (props) => <CustomToolbar {...props} multiTaggable={multiTaggable} rowSelectionModel={rowSelectionModel} refetchTags={refetchTags} />
                    }}
                    slotProps={{
                        toolbar: {
                            printOptions: { disableToolbarButton: true },
                            csvOptions: { disableToolbarButton: true },
                        },
                    }}
                    pagination
                    paginationMode="server"
                    rowCount={data?.total_rows || 0}
                />
            </CardContent>
        </Card>
    );

}